import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { Spinner } from './core';

interface ProtectedRouteProps {
  children: ReactNode;
  /** If provided, the user must have this permission or they are redirected to /. */
  permission?: string;
}

interface PublicOnlyRouteProps {
  children: ReactNode;
}

/**
 * Renders children only when the user is authenticated and (optionally) has the
 * required permission. Shows a spinner while auth or permissions are loading.
 */
export function ProtectedRoute({ children, permission }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const { isLoadingPermissions, hasPermission } = usePermissions();

  if (isLoading || isLoadingPermissions) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-green-50">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (permission && !hasPermission(permission)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/**
 * Renders children only when the user is NOT authenticated (e.g. LoginPage).
 * Redirects authenticated users to / so they never see the login page again.
 */
export function PublicOnlyRoute({ children }: PublicOnlyRouteProps) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-green-50">
        <Spinner size="lg" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
